import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Row,
  Form,
  Spinner,
} from "reactstrap"
import Rating from "components/Rating"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import { useTranslation } from "react-i18next"
import { getDeliveryMan, deleteDeliveryMan, updateDeliveryMan } from "../../../store/delivery-men/actions"
import { DeliveryMan } from "./DeliveryMen"

const DeliveryManDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  // Get data from Redux
  const { selectedDeliveryMan: deliveryMan, loading, error } = useSelector(
    (state: any) => state.DeliveryMen
  )

  const [copied, setCopied] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  // Fetch delivery man on mount
  useEffect(() => {
    if (id) {
      dispatch(getDeliveryMan(Number(id), { push: navigate }))
    }
  }, [dispatch, id, navigate])

  // Delete delivery man - delete directly without confirmation
  const deleteDeliveryManHandler = useCallback(() => {
    if (deliveryMan) {
      dispatch(deleteDeliveryMan(deliveryMan.id, { push: navigate }))
      // Navigate back to list after delete
      dispatch(getDeliveryMan(deliveryMan.id, { push: navigate }))
    }
  }, [dispatch, navigate, deliveryMan])

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      mobile: deliveryMan?.mobile || "",
      address: deliveryMan?.address || "",
      overview: deliveryMan?.overview || "",
      rate: 4,
      isActive: deliveryMan?.isActive ?? true,
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .trim()
        .required(t("deliveryMen_mobileNumberValidationMessage") as string),
      address: Yup.string()
        .trim()
        .required(t("deliveryMen_addressValidationMessage") as string),
      overview: Yup.string().trim(),
      rate: Yup.number().required(
        t("deliveryMen_rateValidationMessage") as string
      ),
      isActive: Yup.boolean(),
    }),
    onSubmit: values => {
      if (deliveryMan) {
        dispatch(updateDeliveryMan(
          deliveryMan.id,
          {
            name: deliveryMan.name,
            mobile: values.mobile,
            address: values.address || "",
            overview: values.overview || "",
            isActive: values.isActive,
          },
          { push: navigate }
        ))
        setIsEdit(false)
      }
    },
  })

  const handleRatingChange = (rating: number) => {
    console.log("Selected rating:", rating)
    validation.setFieldValue("rate", rating)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const toggleHandleChange = async() => {
    dispatch(
      updateDeliveryMan(
        deliveryMan.id,
        {
          name: deliveryMan.name,
          mobile: validation.values.mobile,
          address: validation.values.address || "",
          overview: validation.values.overview || "",
          isActive: !validation.values.isActive,
        },
        { push: navigate }
      )
    )
  }

  // Loading state
  if (loading && !deliveryMan) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner color="primary" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <h5 className="text-danger">{t("deliveryMen_errorLoading") || "Error loading delivery man"}</h5>
            <Button color="primary" onClick={() => navigate("/delivery-men")}>
              {t("common_back") || "Back"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Delivery man not found
  if (!deliveryMan) {
    return (
      <div className="page-content">
        <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <h5>{t("deliveryMan_notFound") || "Delivery man not found"}</h5>
            <Button color="primary" onClick={() => navigate("/delivery-men")}>
              {t("common_back") || "Back"}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard") as string}
          breadcrumbItem={t("deliveryMen_deliveryMen")}
          subBreadcrumbItem={deliveryMan.name}
        />
        <div className="d-flex gap-2">
          {!isEdit ? (
            <Button
              onClick={() => {
                setIsEdit(true)
              }}
              className="bg-gradiant-blue border-0 text-capitalize"
            >
              {t("deliveryMen_editInfo")}
            </Button>
          ) : (
            <Button
              onClick={() => {
                validation.handleSubmit()
              }}
              className="bg-transparent border border-success text-success text-capitalize"
            >
              {t("deliveryMen_saveInfo")}
            </Button>
          )}
          <Button
            onClick={deleteDeliveryManHandler}
            className="bg-transparent border text-danger border-danger text-capitalize"
          >
            {t("deliveryMen_deleteDelivery")}
          </Button>
        </div>
      </div>
      <Card className="shadow-md rounded-3">
        <CardBody>
          <Row>
            <Col xl={4}>
              <div className="product-detai-imgs">
                <div className="rounded-3 border overflow-hidden shadow">
                  <img
                    src="https://img.freepik.com/free-photo/delivery-man-pointing-front_1368-204.jpg?semt=ais_hybrid&w=740"
                    alt=""
                    id="expandedImg1"
                    className="img-fluid mx-auto d-block"
                    style={{ maxWidth: "300px" }}
                  />
                </div>
              </div>
            </Col>

            <Col xl="8">
              <div className="d-flex justify-content-between">
                <div className="">
                  <h4 className="text-semi-dark fw-semibold m-0">
                    {deliveryMan.name}
                  </h4>
                  <div className="d-flex justify-content-between">
                    <p className="text-muted mb-0">
                      <span className="">
                        {t("deliveryMen_dateOfHire") || "Date Of Hire"}
                      </span>
                      <span className="font-size-16 fw-regular text-black px-3">
                        {"__"}
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-semi-dark fw-semibold font-size-16">
                  <Rating
                    totalStars={5}
                    initialValue={validation.values.rate}
                    onChange={handleRatingChange}
                    isDisabled={!isEdit}
                  />
                </p>
              </div>

              <div className="d-flex gap-2 mb-4">
                {["__"].map((address, index) => (
                  <span
                    key={index}
                    className="badge bg-primary-30 text-primary font-size-12 p-2 fw-semibold"
                  >
                    {address}
                  </span>
                ))}
              </div>

              <div className="d-flex justify-content-between mb-4">
                <p className="text-muted mb-0">
                  <span className="font-size-16 fw-semibold text-semi-dark">
                    {t("deliveryMen_deliveryID")}
                  </span>
                  <span className="font-size-16 fw-regular text-black px-3">
                    {deliveryMan.id}
                  </span>
                </p>
                <button
                  className="position-relative cursor-pointer border-0 bg-transparent"
                  onClick={() => {
                    copyToClipboard(deliveryMan.id.toString())
                  }}
                >
                  <i className="bx bx-copy px-2 text-primary bx-sm cursor-pointer"></i>
                  {copied && (
                    <span
                      className="position-absolute  translate-middle-x mt-1 text-success small"
                      style={{ top: "0", left: "-7px" }}
                    >
                      {t("common_copied")}
                    </span>
                  )}
                </button>
              </div>

              {deliveryMan.overview && (
                <div className="text-muted mb-4">
                  <p className="text-semi-dark font-size-14 fw-regular mb-0">
                    {deliveryMan.overview}
                  </p>
                </div>
              )}

              <div className="text-muted mb-4">
                <p className="text-semi-dark font-size-14 fw-regular mb-0">
                  description goes here Lorem ipsum dolor sit amet, consectetur
                  adipiscing elit. Nulla luctus libero urna, at mattis lectus
                  varius at. Cras facilisis purus ipsum, non commodo arcu
                  imperdiet sit amet. Donec bibendum ante eu placerat
                  condimentum.
                </p>
              </div>

              <Form
                onSubmit={e => {
                  e.preventDefault()
                  return false
                }}
              >
                <FormGroup>
                  <FormLabel htmlFor="mobile" required>
                    {t("deliveryMen_mobileNumber")}
                  </FormLabel>

                  <Input
                    readOnly={!isEdit}
                    className="shadow-md"
                    id="mobile"
                    name="mobile"
                    type="text"
                    placeholder={t("deliveryMen_enterMobileNumber") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.mobile || ""}
                    invalid={
                      validation.touched.mobile && validation.errors.mobile
                        ? true
                        : false
                    }
                  />
                  {validation.touched.mobile && validation.errors.mobile ? (
                    <FormFeedback type="invalid">
                      {validation.errors.mobile as string}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="address" required>
                    {t("deliveryMen_address")}
                  </FormLabel>

                  <Input
                    readOnly={!isEdit}
                    id="address"
                    name="address"
                    className="shadow-md"
                    type="text"
                    placeholder={t("deliveryMen_enterAddress") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.address || ""}
                    invalid={
                      validation.touched.address && validation.errors.address
                        ? true
                        : false
                    }
                  />
                  {validation.touched.address && validation.errors.address ? (
                    <FormFeedback type="invalid">
                      {validation.errors.address as string}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <div className="d-flex gap-2 align-items-center mt-4">
                    <p className="text-semi-dark text-start">
                      <b>{t("common_active")}</b>
                      <span> ({t("deliveryMen_campaignInactivePeriod")})</span>
                    </p>
                    <div className="form-check form-switch mb-3">
                      <input
                        type="checkbox"
                        name="isActive"
                        className="form-check-input"
                        checked={validation.values.isActive}
                        onClick={toggleHandleChange}
                        disabled={!isEdit}
                      />
                      <label
                        className="form-check-label text-capitalize"
                        htmlFor="isActive"
                      >
                        {validation.values.isActive ? "Active" : "Inactive"}
                      </label>
                    </div>
                  </div>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  )
}

export default DeliveryManDetails
